




import {
  Vue,
  Component,
  Prop,
  Watch,
} from "@/shared/vuepack";
import * as d3 from "d3";
import { formatCurrency } from "@/shared/currency";
import {
  tint,
  convertColorToHex,
  covertHexToRgbColor
} from "@/shared/colorAdjuster";
import { RgbColor } from "@/shared/types";
import _ from "lodash";
import { useThemeStore } from "@/shared/store/theme";

@Component
export default class ContributionsPieChart extends Vue {
  themeStore = useThemeStore();

  @Prop()
  width: number;

  @Prop()
  height: number;

  @Prop()
  margin: number;

  @Prop()
  maxAllowedContributionForCurrentYear: number;

  @Prop()
  bankDirectContribution: number;

  @Prop()
  employerContribution: number;

  @Prop()
  priorContribution: number;

  @Prop()
  payrollContribution: number;

  @Prop()
  personalContribution: number;

  @Prop()
  planYear: number;

  formatCurrency = formatCurrency;

  getPayrollLegendCopy () {
    return {
      text1: "Employee",
      text2: `${this.formatCurrency(this.payrollContribution)}`
    };
  }

  getEmployerLegendCopy () {
    return {
      text1: "Employer",
      text2: `${this.formatCurrency(this.employerContribution)}`
    };
  }

  getEmployeePersonalLegendCopy () {
    return {
      text1: "Personal",
      text2: `${this.formatCurrency(this.personalContribution)}`
    };
  }

  getPriorLegendCopy () {
    return {
      text1: "Other HSA",
      text2: `${this.formatCurrency(this.priorContribution)}`,
    };
  }

  getBankDirectLegendCopy () {
    return {
      text1: "Contribution",
      text2: `${this.formatCurrency(this.bankDirectContribution)}`,
    };
  }

  mounted () {
    this.drawChart();
  }

  @Watch("$props", { deep: true })
  handleUpdate () {
    this.drawChart();
  }

  drawChart () {
    const maxAllowedContributionForCurrentYear = _.defaultTo(this.maxAllowedContributionForCurrentYear, 0);
    const payrollContribution = _.defaultTo(this.payrollContribution, 0);
    const employerContribution = _.defaultTo(this.employerContribution, 0);
    const personalContribution = _.defaultTo(this.personalContribution, 0);
    const priorContribution = _.defaultTo(this.priorContribution, 0);
    const bankDirectContribution = _.defaultTo(this.bankDirectContribution, 0);

    const amountToMax = (maxAllowedContributionForCurrentYear -
        (payrollContribution + personalContribution + bankDirectContribution + employerContribution + priorContribution)
    );
    const data = [
      payrollContribution,
      employerContribution,
      personalContribution,
      priorContribution,
      bankDirectContribution,
      amountToMax
    ];
    const height = this.height;
    const width = this.width;
    const margin = this.margin ? this.margin : 0;
    const r = 0.4 * width;
    const circleCenter = width / 2 - r / 3 - 5;
    const svg = d3.select(this.$el)
        .html("")
        .attr("height", height + margin)
        .attr("width", width)
        .attr("viewBox", "0 0 " + width + " " + height)
        .append("g");

    const g = svg.append("g")
        .attr("transform", "translate(" + circleCenter + "," + height / 2 + ")");

    const pie = d3.pie().sort(null);

    const path = d3.arc()
        .outerRadius(r - 20)
        .innerRadius(r - 30);

    const arc = g.selectAll(".arc")
        .data(pie(data))
        .enter().append("g");

    const opacity = d3.scaleLinear().range([ 1, 0.1 ]);
    opacity.domain([ 0, data.length - 1 ]);

    arc.append("path")
        .attr("d", path)
        .attr("fill", (d, i) => {
          if (i === 4) {
            return this.lightenSecondaryColor;
          } else if (i === 3) {
            return this.themeStore.tertiaryColor;
          } else if (i === 2) {
            return this.lightenPrimaryColor;
          } else if (i === 1) {
            return this.themeStore.secondaryColor;
          } else if (i === 0) {
            return this.themeStore.primaryColor;
          } else {
            return "#F3F5F6";
          }
        });

    let total = -amountToMax;
    data.map(item => total += item);
    svg.append("text")
        .attr("transform", "translate(" + circleCenter + "," + height / 2 + ")")
        .html(formatCurrency(total))
        .attr("font-size", "1em")
        .style("fill", this.themeStore.tertiaryColor)
        .attr("text-anchor", "middle");

    svg.append("text")
        .attr("transform", "translate(" + circleCenter + "," + (height / 2 + 15) + ")")
        .html(formatCurrency(maxAllowedContributionForCurrentYear))
        .attr("font-size", "0.5em")
        .attr("text-anchor", "middle");

    svg.append("text")
        .attr("transform", "translate(" + circleCenter + "," + (height / 2 + 25) + ")")
        .html(this.planYear + " plan limit")
        .attr("font-size", "0.5em")
        .attr("text-anchor", "middle");
    // y positions are no longer linear scaled, the differening line heights prevent that
    const dist = [ 8, 36, 64, 92, 120 ];
    this.renderLegend(svg,
        this.getPayrollLegendCopy(),
        circleCenter + r,
        dist[0],
        this.themeStore.primaryColor,
        !!payrollContribution);
    this.renderLegend(svg,
        this.getEmployerLegendCopy(),
        circleCenter + r,
        dist[(this.getElementYPosition([
          payrollContribution
        ]))],
        this.themeStore.secondaryColor,
        !!employerContribution);
    this.renderLegend(svg,
        this.getEmployeePersonalLegendCopy(),
        circleCenter + r,
        dist[(this.getElementYPosition([
          payrollContribution,
          employerContribution
        ]))],
        this.lightenPrimaryColor,
        !!personalContribution);
    this.renderLegend(svg,
        this.getPriorLegendCopy(),
        circleCenter + r,
        dist[(this.getElementYPosition([
          payrollContribution,
          employerContribution,
          personalContribution
        ]))],
        this.themeStore.tertiaryColor,
        !!priorContribution);
    this.renderLegend(svg,
        this.getBankDirectLegendCopy(),
        circleCenter + r,
        dist[(this.getElementYPosition([
          payrollContribution,
          employerContribution,
          personalContribution,
          priorContribution
        ]))],
        this.lightenSecondaryColor,
        !!bankDirectContribution);
  }

  get lightenSecondaryColor (): string {
    const secondaryColorRgb = covertHexToRgbColor(this.themeStore.secondaryColor);
    const tintedColor = tint(secondaryColorRgb.r, secondaryColorRgb.g, secondaryColorRgb.b, 0.5);

    return convertColorToHex(tintedColor);
  }

  get lightenPrimaryColor (): string {
    const primaryColorRgb = covertHexToRgbColor(this.themeStore.primaryColor);
    const tintedColor = tint(primaryColorRgb.r, primaryColorRgb.g, primaryColorRgb.b, 0.5);

    return convertColorToHex(tintedColor);
  }

  // Takes the values that could exist before the current item
  // Sums the ones that are greater than 0 to find the y position
  getElementYPosition (previousValues) {
    let collectedPositiveValueCount = 0;
    previousValues.forEach(x => {
      if (x) {
        collectedPositiveValueCount += 1;
      }
    });

    return collectedPositiveValueCount;
  }

  renderLegend (svg, legendDetails, posX, posY, color, display) {
    if (!display) {
      return;
    }
    const legend = svg.append("g")
        .attr("transform", "translate(" + posX + "," + posY + ")");
    legend.append("text").text(legendDetails.text1)
        .attr("font-size", "0.5em");

    if (legendDetails.subCopy) {
      legend.append("text").text(legendDetails.subCopy)
          .attr("font-size", "0.5em")
          .attr("transform", "translate(" + 0 + "," + 8 + ")");

      legend.append("text").text(legendDetails.text2)
          .attr("font-size", "0.5em")
          .attr("font-weight", "700")
          .attr("transform", "translate(" + 0 + "," + 16 + ")");
    } else if (legendDetails.text2) {
      legend.append("text").text(legendDetails.text2)
          .attr("font-size", "0.5em")
          .attr("font-weight", "700")
          .attr("transform", "translate(" + 0 + "," + 8 + ")");
    }

    legend.append("circle")
        .attr("cx", -10)
        .attr("cy", -2)
        .attr("r", 4)
        .attr("fill", color);
  }
}
